import { X, Send, FileText, Eye } from 'lucide-react';
import { colors } from '../styles/colors';
import { formatEuro } from '../utils/formatters';
import { getMoisLabel } from '../utils/constants';
import { useEmailPreview } from '../hooks/useEmailPreview';
import ModalPortal from './ModalPortal';

/**
 * EmailPreviewModal - Modale d'aperçu d'email avant envoi de facture
 *
 * Refactorisé pour utiliser:
 * - useEmailPreview: hook de gestion de l'état et du PDF
 * - formatEuro, getMoisLabel: utilitaires centralisés
 */
function EmailPreviewModal({ isOpen, onClose, facture, prestataire, onConfirm }) {
  const {
    sending,
    pdfUrl,
    loadingPdf,
    showPdfViewer,
    handleSend,
    handleViewPdf,
    handleClosePdfViewer
  } = useEmailPreview(isOpen, facture, onConfirm);

  if (!isOpen || !facture || !prestataire) return null;

  // Données de la facture
  const numeroFacture = facture?.fields?.['N° facture'] || '';
  const mois = facture?.fields?.Mois;
  const annee = facture?.fields?.Année;
  const periode = mois && annee ? `${getMoisLabel(mois)} ${annee}` : '';
  const montantHT = facture?.fields?.['Montant total (HT)'] || 0;
  const tva = facture?.fields?.TVA || 0;
  const montantTTC = facture?.fields?.['Montant total (TTC)'] || (montantHT + tva);
  const prestataireNom = prestataire?.fields?.Nom || prestataire?.nom || '';
  const prestataireEmail = prestataire?.fields?.Email || '';
  const subject = `Facture ${numeroFacture} - ${periode}`;

  return (
    <ModalPortal>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <ModalHeader sending={sending} onClose={onClose} />

          {/* Body */}
          <div style={bodyStyle}>
            <RecipientSection nom={prestataireNom} email={prestataireEmail} />
            <SubjectSection subject={subject} />
            <AttachmentSection
              numeroFacture={numeroFacture}
              prestataireNom={prestataireNom}
              loadingPdf={loadingPdf}
              pdfUrl={pdfUrl}
              onViewPdf={handleViewPdf}
            />
            <MessagePreview
              periode={periode}
              numeroFacture={numeroFacture}
              montantHT={montantHT}
              tva={tva}
              montantTTC={montantTTC}
            />
          </div>

          {/* Footer */}
          <ModalFooter sending={sending} onClose={onClose} onSend={handleSend} />
        </div>

        {/* PDF Viewer Modal */}
        {showPdfViewer && pdfUrl && (
          <PdfViewerModal pdfUrl={pdfUrl} onClose={handleClosePdfViewer} />
        )}
      </div>
    </ModalPortal>
  );
}

// === STYLES ===

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  fontFamily: '"Noto Sans", sans-serif'
};

const modalStyle = {
  backgroundColor: colors.text.white,
  borderRadius: '0.75rem',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  width: '95%',
  maxWidth: '700px',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const bodyStyle = {
  padding: '1.5rem',
  overflowY: 'auto',
  flex: 1
};

const sectionStyle = {
  marginBottom: '1.5rem'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: colors.primary.gray,
  marginBottom: '0.5rem'
};

const fieldStyle = {
  padding: '0.75rem',
  backgroundColor: '#f9fafb',
  borderRadius: '0.375rem',
  border: `1px solid ${colors.secondary.lightGray}`,
  fontSize: '0.875rem'
};

// === SOUS-COMPOSANTS ===

function ModalHeader({ sending, onClose }) {
  return (
    <div style={{
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    }}>
      <div>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: colors.primary.gray }}>
          Aperçu de l'email
        </h2>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: colors.secondary.mediumGray }}>
          Vérifiez les informations avant l'envoi
        </p>
      </div>
      <CloseButton disabled={sending} onClick={onClose} />
    </div>
  );
}

function CloseButton({ disabled, onClick, size = 24 }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '0.5rem',
        color: colors.secondary.mediumGray,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '0.375rem',
        transition: 'background-color 200ms'
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = colors.secondary.lightGray; }}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <X size={size} />
    </button>
  );
}

function RecipientSection({ nom, email }) {
  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>Destinataire :</label>
      <div style={fieldStyle}>
        <div style={{ fontWeight: '600', color: colors.primary.gray }}>{nom}</div>
        <div style={{ color: colors.secondary.mediumGray, marginTop: '0.25rem' }}>{email}</div>
      </div>
    </div>
  );
}

function SubjectSection({ subject }) {
  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>Objet :</label>
      <div style={{ ...fieldStyle, color: colors.primary.gray }}>{subject}</div>
    </div>
  );
}

function AttachmentSection({ numeroFacture, prestataireNom, loadingPdf, pdfUrl, onViewPdf }) {
  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>Pièce jointe :</label>
      <div style={{
        ...fieldStyle,
        color: colors.primary.gray,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: '#dc2626',
            color: colors.text.white,
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            PDF
          </span>
          Facture_Ephyco_{numeroFacture}_{prestataireNom}.pdf
        </div>
        <button
          onClick={onViewPdf}
          disabled={loadingPdf || !pdfUrl}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: colors.primary.teal,
            color: colors.text.white,
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.813rem',
            fontWeight: '500',
            cursor: loadingPdf || !pdfUrl ? 'not-allowed' : 'pointer',
            opacity: loadingPdf || !pdfUrl ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem'
          }}
        >
          <Eye size={16} />
          {loadingPdf ? 'Chargement...' : 'Consulter'}
        </button>
      </div>
    </div>
  );
}

function MessagePreview({ periode, numeroFacture, montantHT, tva, montantTTC }) {
  const cardStyle = {
    backgroundColor: colors.text.white,
    padding: '1rem',
    borderRadius: '0.375rem',
    border: `1px solid ${colors.secondary.lightGray}`,
    marginBottom: '1rem'
  };

  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>Aperçu du message :</label>
      <div style={{
        padding: '1.5rem',
        backgroundColor: '#f9fafb',
        borderRadius: '0.375rem',
        border: `1px solid ${colors.secondary.lightGray}`,
        fontSize: '0.875rem',
        color: colors.primary.gray,
        lineHeight: '1.6'
      }}>
        <p style={{ margin: '0 0 1rem 0' }}>Bonjour,</p>
        <p style={{ margin: '0 0 1rem 0' }}>
          Veuillez trouver ci-joint la facture pour la période de <strong>{periode}</strong>.
        </p>

        {/* Infos facture */}
        <div style={cardStyle}>
          <div style={{ fontWeight: '600', color: colors.primary.teal, marginBottom: '0.5rem' }}>
            Facture N° {numeroFacture}
          </div>
          <div style={{ fontSize: '0.813rem', color: colors.secondary.mediumGray }}>
            Période : {periode}
          </div>
        </div>

        {/* Montants */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>Montant HT</span>
            <span style={{ fontWeight: '600' }}>{formatEuro(montantHT)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span>TVA (20%)</span>
            <span>{formatEuro(tva)}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '0.5rem',
            borderTop: `2px solid ${colors.secondary.lightGray}`,
            fontWeight: '700',
            color: colors.primary.teal
          }}>
            <span>Montant TTC</span>
            <span>{formatEuro(montantTTC)}</span>
          </div>
        </div>

        <p style={{ margin: '0 0 1rem 0' }}>
          Pour toute question concernant cette facture, n'hésitez pas à nous contacter.
        </p>
        <p style={{ margin: 0, fontStyle: 'italic' }}>
          Cordialement,<br />
          <strong>L'équipe Ephyco</strong>
        </p>
      </div>
    </div>
  );
}

function ModalFooter({ sending, onClose, onSend }) {
  return (
    <div style={{
      padding: '1.5rem',
      borderTop: `1px solid ${colors.secondary.lightGray}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      flexShrink: 0
    }}>
      <button
        onClick={onClose}
        disabled={sending}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: colors.text.white,
          color: colors.primary.gray,
          border: `2px solid ${colors.secondary.lightGray}`,
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: sending ? 'not-allowed' : 'pointer',
          opacity: sending ? 0.5 : 1
        }}
      >
        Annuler
      </button>
      <button
        onClick={onSend}
        disabled={sending}
        style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: colors.primary.teal,
          color: colors.text.white,
          border: 'none',
          borderRadius: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: sending ? 'not-allowed' : 'pointer',
          opacity: sending ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <Send size={18} />
        {sending ? 'Envoi en cours...' : 'Envoyer la facture'}
      </button>
    </div>
  );
}

function PdfViewerModal({ pdfUrl, onClose }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.text.white,
          borderRadius: '0.75rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
          width: '95%',
          maxWidth: '1200px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: `1px solid ${colors.secondary.lightGray}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={20} color={colors.primary.teal} />
            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: colors.primary.gray }}>
              Aperçu de la facture
            </h3>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        {/* PDF Iframe */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Aperçu PDF de la facture"
          />
        </div>
      </div>
    </div>
  );
}

export default EmailPreviewModal;
